import { supabase } from '../lib/supabase';
import ProgressMonitorService from './ProgressMonitorService';
import AlertDetectorService from './AlertDetectorService';
import RecoveryEngineService from './RecoveryEngineService';
import ScheduleAdjusterService from './ScheduleAdjusterService';

/**
 * SprintAPIService
 *
 * Central API service for all sprint-related operations.
 * This provides a unified interface for the frontend to interact with the sprint system.
 */
class SprintAPIService {
  // ==========================================
  // SPRINT SCHEDULE OPERATIONS
  // ==========================================

  /**
   * Get the sprint schedule for a student
   * @param {string} userId - Student ID
   * @returns {Promise<Object>} Sprint schedule with all sprints
   */
  async getSchedule(userId) {
    const { data: schedule, error } = await supabase
      .from('sprint_schedules')
      .select('*, sprints(*, sprint_tasks(*, timeline_items(*)), sprint_deliverables(*))')
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    return schedule;
  }

  /**
   * Get today's sprints for a student
   * @param {string} userId - Student ID
   * @returns {Promise<Array>} Array of today's sprints
   */
  async getTodaysSprints(userId) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('sprints')
      .select('*, sprint_tasks(*, timeline_items(*)), sprint_deliverables(*)')
      .eq('user_id', userId)
      .eq('date', todayStr)
      .order('sprint_number', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get currently active sprint
   * @param {string} userId - Student ID
   * @returns {Promise<Object|null>} Active sprint or null
   */
  async getCurrentSprint(userId) {
    const { data, error } = await supabase
      .from('sprints')
      .select('*, sprint_tasks(*, timeline_items(*)), sprint_deliverables(*)')
      .eq('user_id', userId)
      .eq('status', 'IN_PROGRESS')
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data || null;
  }

  // ==========================================
  // SPRINT LIFECYCLE OPERATIONS
  // ==========================================

  /**
   * Start a sprint
   * @param {string} userId - Student ID
   * @param {string} sprintId - Sprint ID
   * @returns {Promise<Object>} Updated sprint
   */
  async startSprint(userId, sprintId) {
    // Update sprint
    const { data: sprint, error } = await supabase
      .from('sprints')
      .update({
        status: 'IN_PROGRESS',
        actual_start_time: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', sprintId)
      .eq('user_id', userId)
      .select('*')
      .single();

    if (error) throw error;

    // Create progress snapshot
    await ProgressMonitorService.createSnapshot(userId);

    // Check for alerts
    await AlertDetectorService.checkAndCreateAlerts(userId);

    return sprint;
  }

  /**
   * Pause a sprint
   * @param {string} userId - Student ID
   * @param {string} sprintId - Sprint ID
   * @returns {Promise<Object>} Updated sprint
   */
  async pauseSprint(userId, sprintId) {
    const { data: sprint, error } = await supabase
      .from('sprints')
      .update({
        status: 'PAUSED',
        updated_at: new Date().toISOString()
      })
      .eq('id', sprintId)
      .eq('user_id', userId)
      .select('*')
      .single();

    if (error) throw error;
    return sprint;
  }

  /**
   * Resume a paused sprint
   * @param {string} userId - Student ID
   * @param {string} sprintId - Sprint ID
   * @returns {Promise<Object>} Updated sprint
   */
  async resumeSprint(userId, sprintId) {
    const { data: sprint, error } = await supabase
      .from('sprints')
      .update({
        status: 'IN_PROGRESS',
        updated_at: new Date().toISOString()
      })
      .eq('id', sprintId)
      .eq('user_id', userId)
      .select('*')
      .single();

    if (error) throw error;
    return sprint;
  }

  /**
   * Complete a sprint
   * @param {string} userId - Student ID
   * @param {string} sprintId - Sprint ID
   * @returns {Promise<Object>} Updated sprint with progress snapshot
   */
  async completeSprint(userId, sprintId) {
    // Update sprint
    const { data: sprint, error } = await supabase
      .from('sprints')
      .update({
        status: 'COMPLETE',
        actual_end_time: new Date().toISOString(),
        completion_percent: 100,
        updated_at: new Date().toISOString()
      })
      .eq('id', sprintId)
      .eq('user_id', userId)
      .select('*')
      .single();

    if (error) throw error;

    // Create progress snapshot
    const snapshot = await ProgressMonitorService.createSnapshot(userId);

    // Check for alerts
    await AlertDetectorService.checkAndCreateAlerts(userId, snapshot);

    return { sprint, snapshot };
  }

  // ==========================================
  // TASK OPERATIONS
  // ==========================================

  /**
   * Update a task status within a sprint
   * @param {string} userId - Student ID
   * @param {string} sprintId - Sprint ID
   * @param {string} taskId - Sprint task ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated task
   */
  async updateTaskStatus(userId, sprintId, taskId, status) {
    // Verify user owns this sprint
    const { data: sprint, error: sprintError } = await supabase
      .from('sprints')
      .select('id')
      .eq('id', sprintId)
      .eq('user_id', userId)
      .single();

    if (sprintError) throw sprintError;

    // Update task
    const updates = {
      status,
      updated_at: new Date().toISOString()
    };

    if (status === 'COMPLETE') {
      updates.completed_at = new Date().toISOString();
    }

    const { data: task, error } = await supabase
      .from('sprint_tasks')
      .update(updates)
      .eq('id', taskId)
      .eq('sprint_id', sprintId)
      .select('*')
      .single();

    if (error) throw error;

    // Update sprint completion percentage
    await this.updateSprintProgress(sprintId);

    return task;
  }

  /**
   * Update sprint completion percentage based on tasks
   * @param {string} sprintId - Sprint ID
   * @returns {Promise<void>}
   */
  async updateSprintProgress(sprintId) {
    // Get all tasks for this sprint
    const { data: tasks, error } = await supabase
      .from('sprint_tasks')
      .select('status')
      .eq('sprint_id', sprintId);

    if (error) throw error;

    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'COMPLETE').length;
    const completionPercent = totalTasks > 0 ? Math.floor((completedTasks / totalTasks) * 100) : 0;

    await supabase
      .from('sprints')
      .update({
        completion_percent: completionPercent,
        updated_at: new Date().toISOString()
      })
      .eq('id', sprintId);
  }

  // ==========================================
  // PROGRESS & MONITORING
  // ==========================================

  /**
   * Get current progress for a student
   * @param {string} userId - Student ID
   * @returns {Promise<Object>} Progress snapshot
   */
  async getCurrentProgress(userId) {
    return await ProgressMonitorService.getCurrentProgress(userId);
  }

  /**
   * Get progress history
   * @param {string} userId - Student ID
   * @param {number} days - Number of days to look back
   * @returns {Promise<Array>} Array of progress snapshots
   */
  async getProgressHistory(userId, days = 7) {
    return await ProgressMonitorService.getProgressHistory(userId, days);
  }

  /**
   * Log session metrics
   * @param {string} userId - Student ID
   * @param {Object} metrics - Session metrics
   * @returns {Promise<Object>} Created session metric
   */
  async logSessionMetrics(userId, metrics) {
    const { data, error } = await supabase
      .from('session_metrics')
      .insert({
        user_id: userId,
        ...metrics,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // ==========================================
  // ALERTS
  // ==========================================

  /**
   * Get alerts for a student
   * @param {string} userId - Student ID
   * @param {Object} filters - Optional filters (severity, unread, dismissed)
   * @returns {Promise<Array>} Array of alerts
   */
  async getAlerts(userId, filters = {}) {
    return await AlertDetectorService.getAlerts(userId, filters);
  }

  /**
   * Mark alert as read
   * @param {string} alertId - Alert ID
   * @returns {Promise<Object>} Updated alert
   */
  async markAlertAsRead(alertId) {
    return await AlertDetectorService.markAsRead(alertId);
  }

  /**
   * Dismiss an alert
   * @param {string} alertId - Alert ID
   * @returns {Promise<Object>} Updated alert
   */
  async dismissAlert(alertId) {
    return await AlertDetectorService.dismissAlert(alertId);
  }

  // ==========================================
  // RECOVERY & ADJUSTMENTS
  // ==========================================

  /**
   * Generate a recovery plan
   * @param {string} userId - Student ID
   * @param {string} triggerAlertId - Optional alert that triggered recovery
   * @returns {Promise<Object>} Recovery plan with options
   */
  async generateRecoveryPlan(userId, triggerAlertId = null) {
    return await RecoveryEngineService.generateRecoveryPlan(userId, triggerAlertId);
  }

  /**
   * Get recovery plans for a student
   * @param {string} userId - Student ID
   * @param {string} status - Optional status filter
   * @returns {Promise<Array>} Array of recovery plans
   */
  async getRecoveryPlans(userId, status = null) {
    return await RecoveryEngineService.getRecoveryPlans(userId, status);
  }

  /**
   * Apply a recovery option
   * @param {string} userId - Student ID
   * @param {string} optionId - Recovery option ID
   * @returns {Promise<Object>} Adjustment result
   */
  async applyRecoveryOption(userId, optionId) {
    const result = await ScheduleAdjusterService.applyRecoveryOption(userId, optionId);

    // Create progress snapshot after adjustment
    await ProgressMonitorService.createSnapshot(userId);

    return result;
  }

  /**
   * Get adjustment history
   * @param {string} userId - Student ID
   * @returns {Promise<Array>} Array of adjustments
   */
  async getAdjustmentHistory(userId) {
    const { data: schedule, error: scheduleError } = await supabase
      .from('sprint_schedules')
      .select('id')
      .eq('user_id', userId)
      .single();

    if (scheduleError) throw scheduleError;

    return await ScheduleAdjusterService.getAdjustmentHistory(schedule.id);
  }

  /**
   * Undo a recovery plan
   * @param {string} userId - Student ID
   * @param {string} recoveryPlanId - Recovery plan ID
   * @returns {Promise<Object>} Undo result
   */
  async undoRecoveryPlan(userId, recoveryPlanId) {
    const result = await ScheduleAdjusterService.undoRecoveryPlan(userId, recoveryPlanId);

    // Create progress snapshot after undo
    await ProgressMonitorService.createSnapshot(userId);

    return result;
  }

  // ==========================================
  // MILESTONES
  // ==========================================

  /**
   * Get milestones for a student
   * @param {string} userId - Student ID
   * @param {string} status - Optional status filter
   * @returns {Promise<Array>} Array of milestones
   */
  async getMilestones(userId, status = null) {
    let query = supabase
      .from('milestones')
      .select('*, sprint_schedules!inner(user_id)')
      .eq('sprint_schedules.user_id', userId);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query.order('target_date', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  /**
   * Update milestone status
   * @param {string} milestoneId - Milestone ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated milestone
   */
  async updateMilestoneStatus(milestoneId, status) {
    const updates = {
      status,
      updated_at: new Date().toISOString()
    };

    if (status === 'COMPLETE') {
      updates.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('milestones')
      .update(updates)
      .eq('id', milestoneId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // ==========================================
  // HELPER METHODS
  // ==========================================

  /**
   * Manually trigger alert check
   * @param {string} userId - Student ID
   * @returns {Promise<Array>} Array of new alerts
   */
  async checkAlerts(userId) {
    return await AlertDetectorService.checkAndCreateAlerts(userId);
  }

  /**
   * Create a manual progress snapshot
   * @param {string} userId - Student ID
   * @returns {Promise<Object>} Progress snapshot
   */
  async createProgressSnapshot(userId) {
    return await ProgressMonitorService.createSnapshot(userId);
  }
}

export default new SprintAPIService();
